import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuy.skin';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Itaobuy Spreadsheet Shopping Guide - Step-by-Step Sourcing Tutorial',
  description: 'A complete step-by-step guide to sourcing products from China using the itaobuy spreadsheet system. Follow these five simple steps to start buying smarter.',
  image: 'https://readdy.ai/api/search-image?query=Organized%20desk%20workspace%20with%20laptop%20displaying%20shopping%20interface%20surrounded%20by%20product%20samples%20fashion%20items%20and%20shipping%20materials%20on%20light%20wooden%20surface%20bright%20natural%20window%20lighting%20professional%20e-commerce%20atmosphere&width=1600&height=900&seq=shopguide-hero-z01&orientation=landscape',
  datePublished: '2026-06-28',
  dateModified: '2026-06-28',
  author: {
    '@type': 'Organization',
    name: 'Itaobuy Sheet Team',
    url: siteUrl,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Itaobuy Sheet',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg',
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}/shopping-guide`,
  },
  wordCount: 1200,
  timeRequired: 'PT5M',
  inLanguage: 'en',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
    { '@type': 'ListItem', position: 2, name: 'Shopping Guide', item: `${siteUrl}/shopping-guide` },
  ],
};

const faqItems = [
  {
    q: 'How long does the entire process take from order to delivery?',
    a: 'Most orders are delivered within 2-4 weeks. Domestic supplier shipping to the warehouse takes 3-7 days, quality inspection takes 1-2 days, and international express shipping takes 5-10 business days to most countries.',
  },
  {
    q: 'What payment methods are accepted?',
    a: 'The itaobuy spreadsheet platform accepts major international credit cards (Visa, Mastercard, American Express), PayPal, and bank transfers for larger orders. All payments are processed through secure, encrypted channels.',
  },
  {
    q: 'Is there a minimum order quantity?',
    a: 'No, there is no minimum order quantity. You can order a single item or hundreds of items. However, ordering multiple items together significantly reduces per-item shipping costs through consolidation.',
  },
  {
    q: 'What if I receive a defective or incorrect item?',
    a: 'The quality inspection process catches most issues before items leave China. If a problem is found during inspection, you can request an exchange or refund. For rare issues discovered after delivery, the support team assists with resolution.',
  },
  {
    q: 'Can I track my package during international shipping?',
    a: 'Yes, tracking numbers are provided for all international shipments and updated in your spreadsheet. You can monitor your package progress from the consolidation warehouse to your doorstep.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.a,
    },
  })),
};

const steps = [
  {
    step: '01',
    title: 'Browse the Product Catalog',
    description: 'Visit the itaobuy spreadsheet at kaspreadsheet.com and explore 14 product categories including Shoes, Hoodies, T-Shirts, Jackets, Accessories, and more. Each product listing includes pricing, available variants, and supplier details.',
    icon: 'ri-search-line',
  },
  {
    step: '02',
    title: 'Compile Your Order List',
    description: 'Copy the product links for items you want to purchase into your order sheet. Specify quantities, sizes, colors, and any special requirements for each item. Double-check your list before submission.',
    icon: 'ri-list-check-3',
  },
  {
    step: '03',
    title: 'Submit and Confirm',
    description: 'Submit your compiled order through the platform. A dedicated agent reviews your selections, confirms availability and pricing with each supplier, and provides a final quote including product costs and estimated shipping.',
    icon: 'ri-send-plane-line',
  },
  {
    step: '04',
    title: 'Quality Inspection',
    description: 'All items arrive at the consolidation warehouse where trained inspectors check each product for quality, accuracy, and defects. Photographs are taken for your review before international shipping.',
    icon: 'ri-shield-check-line',
  },
  {
    step: '05',
    title: 'International Shipping',
    description: 'After inspection and your approval, all items are consolidated into a single package and shipped internationally with full tracking and insurance. Delivery typically takes 5-15 business days depending on your location.',
    icon: 'ri-ship-line',
  },
];

const faqs = faqItems;

export default function ShoppingGuide() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <JsonLd data={faqSchema} />
      <Navbar />
      <main>
        <article className="pt-24 pb-12 px-6 md:px-10">
          <div className="max-w-[900px] mx-auto">
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-foreground-500">
                <li>
                  <Link to="/" className="hover:text-foreground-800 transition-colors duration-150">
                    Home
                  </Link>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </span>
                  <span className="text-foreground-800">Shopping Guide</span>
                </li>
              </ol>
            </nav>

            <header className="mb-12 text-center">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
                How It Works
              </span>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                Itaobuy Spreadsheet Shopping Guide
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-[650px] mx-auto leading-relaxed">
                A complete step-by-step guide to sourcing products from China using the itaobuy spreadsheet system. Follow these five simple steps to start buying smarter.
              </p>
            </header>

            <div className="relative w-full h-[350px] md:h-[450px] rounded-2xl overflow-hidden mb-12">
              <img
                src="https://readdy.ai/api/search-image?query=Organized%20desk%20workspace%20with%20laptop%20displaying%20shopping%20interface%20surrounded%20by%20product%20samples%20fashion%20items%20and%20shipping%20materials%20on%20light%20wooden%20surface%20bright%20natural%20window%20lighting%20professional%20e-commerce%20atmosphere&width=1600&height=900&seq=shopguide-hero-z01&orientation=landscape"
                alt="Itaobuy spreadsheet shopping guide"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="space-y-5 mb-16">
              {steps.map((s) => (
                <div key={s.step} className="flex gap-5 p-6 rounded-2xl bg-background-100 transition-colors hover:bg-accent-50">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-foreground-950 text-background-50 flex items-center justify-center font-bold text-sm">
                    {s.step}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-foreground-900 mb-1.5">{s.title}</h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">{s.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-accent-50 rounded-2xl p-6 md:p-10 text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                Ready to Start Shopping?
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-[550px] mx-auto leading-relaxed mb-6">
                The itaobuy spreadsheet system is live and ready for your first order. Browse 14 categories of products and experience a faster way to source from China.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold cursor-pointer hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap"
              >
                Go to Kaspreadsheet
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line"></i>
                </span>
              </a>
            </div>

            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-3">
                {faqs.map((faq) => (
                  <details key={faq.q} className="group bg-background-100 rounded-xl p-5 cursor-pointer">
                    <summary className="flex items-center justify-between text-foreground-800 font-medium text-sm list-none">
                      {faq.q}
                      <span className="w-5 h-5 flex items-center justify-center transition-transform duration-200 group-open:rotate-45">
                        <i className="ri-add-line"></i>
                      </span>
                    </summary>
                    <p className="mt-3 text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/60">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-foreground-600 text-sm hover:text-foreground-900 transition-colors duration-150"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </span>
                Back to Homepage
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}